<template>
  <!-- The rest of the template remains unchanged -->
  <div class="chat-wrapper d-flex">
    <!-- Sidebar -->
    <v-navigation-drawer v-model="sidebarOpen" app class="sidebar" width="300">
      <v-list>
        <!-- Top Row with Icons -->
        <v-list-item>
          <v-row align="center" class="px-2 d-flex justify-space-between">
            <!-- Collapse Button -->
            <span @click="toggleSidebar">
              <v-icon>$mdiChevronDoubleLeft</v-icon>
            </span>
            <!-- Search Button -->
            <span icon @click="openSearch">
              <v-icon>$mdiMagnify</v-icon>
            </span>
            <!-- New Chat Button -->

            <v-btn icon @click="startNewChat">
              <v-icon>$mdiSquareEditOutline</v-icon>
            </v-btn>
          </v-row>
        </v-list-item>

        <v-divider></v-divider>

        <!-- Chat History -->
        <v-list dense>
          <v-list-item
            v-for="chat in chatHistory"
            :key="chat.id"
            @click="loadChat(chat.id)"
            class="chat-list-item"
            @mouseenter="hoveredChatId = chat.id"
            @mouseleave="hoveredChatId = null"
          >
              <!-- Tooltip on Title Hov er -->
              <v-list-item-title
                class="text-truncate chat-title"
                v-tooltip.bottom="chat.title"
              >
                {{ chat.title }}
              </v-list-item-title>

              <!-- 3-dot menu (lightweight icon) -->
              <span
                v-if="hoveredChatId === chat.id"
                @click.stop="openMenu($event, chat.id)"
              >
                <v-icon small class="text-grey-darken-1"
                  >$mdiDotsHorizontal</v-icon
                >
              </span>
           
          </v-list-item>
        </v-list>
      </v-list>
    </v-navigation-drawer>
    <!-- Chat Options Menu -->
    <v-menu
      v-model="menu.show"
      :position-x="menu.x"
      :position-y="menu.y"
      absolute
      attach="body"
    >
      <v-list dense>
        <v-list-item @click="shareChat(menu.chatId)">
          <v-list-item-title>Share</v-list-item-title>
        </v-list-item>
        <v-list-item @click="renameChat(menu.chatId)">
          <v-list-item-title>Rename</v-list-item-title>
        </v-list-item>

        <v-list-item @click="deleteChat(menu.chatId)">
          <v-list-item-title class="text-error">Delete</v-list-item-title>
        </v-list-item>
        <!--  <v-list-item @click="archiveChat(menu.chatId)">
          <v-list-item-title>Archive</v-list-item-title>
        </v-list-item>-->
      </v-list>
    </v-menu>
    <!-- Rename Chat Dialog -->
    <v-dialog v-model="renameDialog.show" max-width="400px">
      <v-card>
        <v-card-title>Rename Chat</v-card-title>
        <v-card-text>
          <v-text-field v-model="renameDialog.newTitle" label="New Chat Name" />
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="renameDialog.show = false">Cancel</v-btn>
          <v-btn color="primary" @click="confirmRename">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Main Chat Area -->
    <v-card class="chat-container">
      <!--<google-sign-in/>-->

      <!-- Sidebar Toggle (When Collapsed) -->
      <span
        v-if="!sidebarOpen"
        class="toggle-sidebar-btn"
        icon
        @click="toggleSidebar"
      >
        <v-icon>$mdiChevronDoubleRight</v-icon>
      </span>

      <!-- Chat Header -->
      <div class="chat-header">
        <v-card-title class="primary white--text">{{ title }}</v-card-title>
        <v-card-subtitle class="primary white--text">This guy can analyze 80,000 plus companies across the Globe. Deep dive balance sheets, PnL, Cash Flow, going back 30  years (Enterprise Version). 
          <br>Find investment opportunities by looking at hundreds of financial ratios. 
        <br>Unearth patterns in Share holdings, combine custom technical indicators like candle patterns, RSI and others. 
        <br>Ask it to create adhoc queries across multiple dimensions spanning asset classes - Equities, FnO, Forex,   Commodities, Crypto</v-card-subtitle>
      </div>
      <v-expansion-panels color="white">
        <v-expansion-panel title="Datasets to Deep Dive">
          <v-expansion-panel-text>
            Mix and Match from dozens of signals, to discover mispriced investment/trading opportunities.
            While ChatGPT and others are public scrapers, this is an NLP/SLM tool that runs on private structured/unstructured data, and no data goes out.
            <div style="max-height: 500px; overflow-y: auto">
              <data-explorer-grid :categorizedArrays="categorizedArrays" />
            </div>
          </v-expansion-panel-text>
        </v-expansion-panel>
        <v-expansion-panel title="How to Query">
          <v-expansion-panel-text>
            Company attributes like market cap, market cap type (Small Cap, Mid
            Cap, Large Cap), sector name, can be used to narrow down companies, and then query across any other signals.
            Be specific with Proper Nouns like sector names, field names. Specific instructions work better than generic - e.g. 
            instead of "Which companies have good FCF Margin" rephrase as "Analyze TTM ratios, and report which companies have FCF Yield above  30%". See Examples under each DataSet category.
            Remember, AI is like an enthusiastic new junior engineer, who will always do *something* to impress  😎, so be specific with instructions.
          </v-expansion-panel-text>
        </v-expansion-panel>
        <v-expansion-panel title="Your Saved Prompts">
          <v-expansion-panel-text>
            <div style="max-height: 500px; overflow-y: auto">
              <saved-prompts :userEmail="userGoogle.email" />
            </div>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
      <!-- Messages Area -->
      <div class="messages-wrapper" ref="messagesContainer">
        <v-card-text class="chat-messages">
          <v-list two-line>
            <template v-for="(message, index) in messages" :key="index">
              <v-list-item
                :class="message.role === 'user' ? 'justify-end' : ''"
                class="message-item"
              >
                <v-card
                  :class="[
                    'message-card',
                    message.role === 'user'
                      ? 'user-message'
                      : 'assistant-message',
                  ]"
                  elevation="0"
                >
                  <v-card-title class="text-caption pb-1">
                    {{ message.role === "user" ? "You" : "Assistant" }}
                  </v-card-title>
                  <v-card-text
                    class="white-space-pre pt-0"
                    v-html="message.content"
                  ></v-card-text>
                  <v-btn
                    color="primary"
                    v-if="message.is_alert_set === true"
                    @click="setAlert(message.id, message.chat_uuid, false)"
                    >Remove Report</v-btn
                  >
                  <v-btn
                    color="primary"
                    v-if="
                      message.role === 'assistant' &&
                      message.is_alert_set !== true
                    "
                    @click="setAlert(message.id, message.chat_uuid, true)"
                    >Save</v-btn
                  >
                  <span
                    v-if="
                      message.role === 'assistant' &&
                      message.is_alert_set !== true
                    "
                    class="ml-4"
                    ><v-icon
                      data-action="show-snackbar"
                      data-message="Commonly used Prompts can be Saved and run as Reports. Further, you may schedule them to act as Investing/Trading Alerts."
                      >$mdiInformationOutline</v-icon
                    ></span
                  >
                </v-card>
              </v-list-item>
            </template>

            <!-- Loading indicator -->
            <v-list-item v-if="isLoading">
                <v-list-item-subtitle>
                  <v-progress-circular
                    indeterminate
                    color="primary"
                    size="24"
                    class="mr-2"
                  />
                  Fetching Your Response...
                </v-list-item-subtitle>
              </v-list-item>
          </v-list>
        </v-card-text>
      </div>

      <!-- Input Area with enhanced text area -->
      <div class="chat-input">
        <v-card-actions class="pa-4">
          <div class="position-relative w-100">
            <v-textarea
              ref="chatInput"
              v-model="userInput"
              :label="userInputLabel"
              :disabled="isLoading || !$store.getters.isAuthenticated"
              variant="outlined"
              density="comfortable"
              hide-details
              rows="2"
              auto-grow
              @click:append-inner="sendMessage"
              :prepend-inner-icon="userInput ? '$mdiTrashCan' : ''"
              @click:prepend-inner="userInput = ''"
              @keydown="handleKeydown"
              @input="handleInput"
              @click="updateCursorPosition"
              class="suggestion-container"
            />
            <!-- Inline suggestion that appears next to the word being typed -->
            <div v-if="activeSuggestion && suggestionPosition" 
                 class="suggestion-inline"
                 :style="{
                   top: `${suggestionPosition.top}px`,
                   left: `${suggestionPosition.left}px`
                 }">
              <span class="suggestion-text">{{ activeSuggestion.substring(currentWord.length) }}</span>
            </div>
          </div>
          <span v-if="isMicSupported"><v-icon @click="startSpeechRecognition">$mdiMicrophone</v-icon></span>
          <span v-if="userInput.trim()"><v-icon @click="sendMessage">$mdiSend</v-icon></span>
        </v-card-actions>
      </div>
    </v-card>
   <!-- Search Modal -->
    <v-dialog v-model="searchDialog" max-width="500px">
      <v-card>
        <v-card-title>
          <v-text-field
            v-model="searchQuery"
            label="Search Chats"
            clearable
            prepend-inner-icon="mdi-magnify"
          />
        </v-card-title>
        <v-divider />
        <v-card-text>
          <v-list dense>
            <v-list-item
              v-for="chat in filteredChats"
              :key="chat.id"
              @click="selectChat(chat.id)"
            >
                <v-list-item-title>{{ chat.title }}</v-list-item-title>
              </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-btn color="primary" text @click="searchDialog = false"
            >Close</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :timeout="snackbar.timeout"
      :color="snackbar.color"
      @update:modelValue="startProgress"
    >
      {{ snackbar.message }}
      <v-progress-linear
        :model-value="progress"
        color="black"
        height="4"
        class="mt-2"
      ></v-progress-linear>
    </v-snackbar>
    <v-dialog v-model="showReportTitleDialog" max-width="400px">
      <v-card>
        <v-card-title>Save Prompt</v-card-title>
        <v-card-text>
          Save this Prompt as a Report
          <v-text-field
            v-model="savedReportTitle"
            label="Title"
            outlined
            dense
            class="mt-3"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="saveReport">OK</v-btn>
          <v-btn color="secondary" @click="showReportTitleDialog = false"
            >Cancel</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showUnSaveDialog" max-width="400px">
      <v-card>
        <v-card-title>Remove Report</v-card-title>
        <v-card-text>
          Prompt will remain, Saved Report will be deleted.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="saveReport">OK</v-btn>
          <v-btn color="secondary" @click="showUnSaveDialog = false"
            >Cancel</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  USER_CHAT_HISTORY,
  SAVE_USER_CHAT,
  UNSAVE_USER_CHAT,
  USER_CHAT_MESSAGES_FOR_ID,
} from "../lib/helper/queries";
import { mapState } from "vuex";
import DataExplorerGrid from "./DataExplorerGrid.vue";
import SavedPrompts from "./SavedPrompts.vue";

export default {
  name: "Assistant",
  components: {
    DataExplorerGrid,
    SavedPrompts,
  },
  async mounted() {
    if (this.userGoogle && this.userGoogle.email) {
      await this.initData();
    }
    this.isMicSupported = 'webkitSpeechRecognition' in window;
    document.addEventListener("click", this.handleButtonClick);
    
    // Fetch keywords from API
    //await this.fetchKeywords();

    // Set up an event listener for text selection and cursor movement
    window.addEventListener('resize', this.recalculateSuggestionPosition);
  },
  beforeUnmount() {
    //document.removeEventListener("click", this.handleButtonClick);
    window.removeEventListener('resize', this.recalculateSuggestionPosition);
  },
  data() {
    return {
      expanded: true,
      categorizedArrays: {},
      promptQLFields: [],
      sidebarOpen: false,
      hoveredChatId: null,
      menu: {
        show: false,
        x: 0,
        y: 0,
        chatId: null,
      },
      renameDialog: {
        show: false,
        chatId: null,
        newTitle: "",
      },
      searchQuery: "",
      searchDialog: false,
      chatHistory: [],
      timeTaken: 0,
      messages: [],
      isLoading: false,
      userInput: "",
      suggestions: [],
      company_names_list: [],
      hideNoData: true,
      snackbar: {
        show: false,
        message: "",
        timeout: 3000,
        color: "orange",
      },
      progress: 100,
      interval: null,
      distilledModel: "reasoning_dseek",
      title: "Capital Markets Research Agent",
      savedReportTitle: "",
      userInputLabel: "This is an AI tool. Double Check",
      saveAsReport: { chat_id: "", chat_uuid: "", is_alert_set: false },
      showReportTitleDialog: false,
      showUnSaveDialog: false,
      isMicSupported: false,
      keywords: [], // Array to store keywords fetched from API
      currentWord: "", // Current word being typed
      cursorPosition: 0, // Current cursor position in the textarea
      activeSuggestion: "", // Current active keyword suggestion
      suggestionPosition: null, // Position for the suggestion overlay
    };
  },
  watch: {
    async userGoogle(newVal) {
      console.log("userGoogle", newVal);
      if (newVal && newVal.email) {
        await this.initData();
      }
    },
    "snackbar.show"(newVal) {
      if (newVal) {
        this.startProgress();
      } else {
        clearInterval(this.interval);
      }
    },
    userInput() {
      // Recalculate suggestion position when user input changes
      this.$nextTick(() => {
        if (this.activeSuggestion) {
          this.calculateSuggestionPosition();
        }
      });
    }
  },
  methods: {
    async initData() {
      const chatSessionId = crypto.randomUUID();
      sessionStorage.setItem("chatSessionId", chatSessionId);
      try {
        const response = await fetch("/api/chat/promptcategories", {});
        this.categorizedArrays = await response.json();
        await this.getUserChatHistory();
      } catch (e) {
        console.error(e);
      }
    },
  decodeUnicodeEscapes(str) {
  // Only decode if it contains double-escaped unicode like \\uXXXX
  if (/\\\\u[0-9a-fA-F]{4}/.test(str)) {
    try {
      // Replace \\uXXXX with \uXXXX
      const singleEscaped = str.replace(/\\\\u/g, '\\u');
      // Decode
      return JSON.parse(`"${singleEscaped}"`);
    } catch (e) {
      console.warn('Unicode decode failed:', e);
      return str;
    }
  }
  return str;
},


    // Updated method to fetch keywords with authorization headers
    async fetchKeywords() {
      try {
        const response = await fetch("/api/chat/keywords", {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        if (response.ok) {
          this.keywords = await response.json();
          console.log("Fetched keywords:", this.keywords);
        } else {
          console.error("Failed to fetch keywords:", response.statusText);
        }
      } catch (error) {
        console.error("Error fetching keywords:", error);
      }
    },
    
    // Handle clicking in the textarea to update cursor position
    updateCursorPosition() {
      this.$nextTick(() => {
        const inputEl = this.$refs.chatInput.$el.querySelector('textarea');
        if (inputEl) {
          this.cursorPosition = inputEl.selectionStart;
          if (this.activeSuggestion) {
            this.calculateSuggestionPosition();
          }
        }
      });
    },
    
    // Calculate position for suggestion overlay
    calculateSuggestionPosition() {
      const inputEl = this.$refs.chatInput.$el.querySelector('textarea');
      if (!inputEl) return;
      
      const prependInnerEl = this.$refs.chatInput.$el.querySelector('.v-field__prepend-inner');
    
  // Calculate any adjustment needed based on the prepend icon
  let prependAdjustment = 0;
  if (prependInnerEl && this.userInput) {
    const prependRect = prependInnerEl.getBoundingClientRect();
    const iconElement = prependInnerEl.querySelector('i') || prependInnerEl.querySelector('svg');
    if (iconElement) {
      const iconRect = iconElement.getBoundingClientRect();
      prependAdjustment = iconRect.width 
        + parseFloat(window.getComputedStyle(iconElement).marginRight || 0);
    } else {
      prependAdjustment = prependRect.width;
    }
  }
      // Create a temporary mirror element to calculate text dimensions
      const mirror = document.createElement('div');
      const inputStyle = window.getComputedStyle(inputEl);
      
      // Copy styles from textarea to the mirror
      mirror.style.position = 'absolute';
      mirror.style.top = '0';
      mirror.style.left = '0';
      mirror.style.visibility = 'hidden';
      mirror.style.whiteSpace = 'pre-wrap';
      mirror.style.wordWrap = 'break-word';
      mirror.style.width = inputStyle.width;
      mirror.style.padding = inputStyle.padding;
      mirror.style.border = inputStyle.border;
      mirror.style.lineHeight = inputStyle.lineHeight;
      mirror.style.fontFamily = inputStyle.fontFamily;
      mirror.style.fontSize = inputStyle.fontSize;
      
      // Get text before cursor and replace spaces with non-breaking spaces to preserve them
      const textBeforeCursor = this.userInput.substring(0, this.cursorPosition).replace(/ /g, '\u00a0');
      
      // Position the caret indicator at the end
      mirror.innerHTML = textBeforeCursor + '<span id="caret"></span>';
      document.body.appendChild(mirror);
      
      // Get position of the caret
      const caret = mirror.querySelector('#caret');
      const caretRect = caret.getBoundingClientRect();
      const inputRect = inputEl.getBoundingClientRect();
      
      // Calculate the position relative to the textarea
      const top = caretRect.top + inputEl.scrollTop - 2;
      const left = caretRect.left + inputRect.left + inputEl.scrollLeft - (prependAdjustment ? prependAdjustment : 0) + 2;
  
      
      // Remove the mirror element
      document.body.removeChild(mirror);
      
      // Set suggestion position
      this.suggestionPosition = { top, left };
    },
    
    // Recalculate position when window resizes
    recalculateSuggestionPosition() {
      if (this.activeSuggestion) {
        this.calculateSuggestionPosition();
      }
    },
    
    // Handle input changes to detect when to show keyword suggestions
    handleInput(event) {
      if (!this.keywords || !this.keywords.length) return;
      
      const inputEl = this.$refs.chatInput.$el.querySelector('textarea');
      if (!inputEl) return;
      
      // Get current cursor position
      this.cursorPosition = inputEl.selectionStart;
      
      // Get the text before the cursor
      const textBeforeCursor = this.userInput.substring(0, this.cursorPosition);
      
      // Find the word being typed (from last space/newline to cursor)
      const lastSpaceIndex = Math.max(
        textBeforeCursor.lastIndexOf(' '), 
        textBeforeCursor.lastIndexOf('\n')
      );
      
      this.currentWord = textBeforeCursor.substring(lastSpaceIndex + 1);
      
      // Only search for matches if the current word has at least 3 characters
      if (this.currentWord.length >= 4) {
        // Find matching keywords
        const matches = this.keywords.filter(keyword => 
          keyword.toLowerCase().indexOf(this.currentWord.toLowerCase()) > -1
        );
        
        if (matches.length > 0) {
          this.activeSuggestion = matches[0]; // Use the first match
          this.$nextTick(() => {
            this.calculateSuggestionPosition();
          });
        } else {
          this.activeSuggestion = "";
          this.suggestionPosition = null;
        }
      } else {
        this.activeSuggestion = "";
        this.suggestionPosition = null;
      }
    },
    
    // Handle keyboard events for the textarea
    handleKeydown(event) {
      // Handle Tab key for accepting suggestions
      if (event.key === 'Tab' && this.activeSuggestion) {
        event.preventDefault(); // Prevent default tab behavior
        
        // Replace the current word with the suggested keyword
        const textBeforeCursor = this.userInput.substring(0, this.cursorPosition - this.currentWord.length);
        const textAfterCursor = this.userInput.substring(this.cursorPosition);
        
        this.userInput = textBeforeCursor + this.activeSuggestion + textAfterCursor;
        
        // Update cursor position to be after the inserted keyword
        this.$nextTick(() => {
          const inputEl = this.$refs.chatInput.$el.querySelector('textarea');
          if (inputEl) {
            const newPosition = textBeforeCursor.length + this.activeSuggestion.length;
            inputEl.setSelectionRange(newPosition, newPosition);
            this.cursorPosition = newPosition;
          }
          this.activeSuggestion = "";
          this.suggestionPosition = null;
        });
      }
      // Handle Ctrl+Enter or Cmd+Enter for sending message
      else if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
        event.preventDefault(); // Prevents adding a new line
        this.sendMessage();
      }
    },

    // Existing methods
    toggleSidebar() {
      this.sidebarOpen = !this.sidebarOpen;
    },
    
    // Rest of the existing methods unchanged...
    async getUserChatHistory() {
      const chatQueryResponse = await this.$apollo.query({
        query: USER_CHAT_HISTORY,
        variables: { email: this.userGoogle.email },
        fetchPolicy: "no-cache",
      });
      this.chatHistory = [];
      for (const chat of chatQueryResponse.data.user_chat) {
        this.chatHistory.push({
          id: chat.chat_uuid,
          title: chat.chat_title,
        });
      }
    },
    startNewChat() {
      const chatSessionId = crypto.randomUUID();
      sessionStorage.setItem("chatSessionId", chatSessionId);
      this.messages = []; // Clear messages for a new session
      this.title = "New Chat";
    },
    async loadChat(chatId) {
      this.messages = [];
      sessionStorage.setItem("chatSessionId", chatId);
      try {
        const resp = await this.$apollo.query({
          query: USER_CHAT_MESSAGES_FOR_ID,
          variables: { email: this.userGoogle.email, chat_uuid: chatId },
          fetchPolicy: "no-cache",
        });
        const chats = resp.data.user_chat;
        for (const chat of chats) {
          this.messages.push({
            role: "user",
            content: chat.text_content_user_query,
          });
          const assistantRecord = {
            role: "assistant",
            id: chat.id,
            chat_uuid: chat.chat_uuid,
            is_alert_set: chat.is_alert_set,
            content: chat.text_content_assistant_formatted_response,
          };

          this.messages.push(assistantRecord);
        }
      } catch (e) {
        console.error(e);
      }
    },
    openMenu(event, chatId) {
      // Prevent the default event and stop propagation
      event.preventDefault();
      event.stopPropagation();
      // Set the position based on the mouse click
      this.menu.x = event.clientX;
      this.menu.y = event.clientY;
      this.menu.chatId = chatId;
      this.menu.show = true;
    },
    async shareChat(chatId) {
      try {
        const response = await fetch("/api/chat/share", {
          method: "POST",

          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            chat_uuid: chatId,
          }),
        });
        if (response.status !== 200) {
          this.snackbar.color = "error";
          this.snackbar.message = "Not authorized";
          this.snackbar.show = true;
          return;
        }
        const data = await response.json();
        if (data.url && data.url.endsWith(chatId))
          window.open(data.url, "_blank");
      } catch (e) {
        console.error(e);
      }
    },
    renameChat(chatId) {
      const chat = this.chatHistory.find((c) => c.id === chatId);
      if (chat) {
        this.renameDialog.chatId = chatId;
        this.renameDialog.newTitle = chat.title;
        this.renameDialog.show = true;
      }
    },
    confirmRename() {
      const chat = this.chatHistory.find(
        (c) => c.id === this.renameDialog.chatId
      );
      if (chat) {
        chat.title = this.renameDialog.newTitle;
        this.renameDialog.show = false;
      }
    },
    archiveChat(chatId) {
      console.log("Archiving chat:", chatId);
    },
    deleteChat(chatId) {
      this.chatHistory = this.chatHistory.filter((chat) => chat.id !== chatId);
    },
    selectChat(chatId) {
      this.loadChat(chatId);
      this.searchDialog = false;
    },
    openSearch() {
      this.searchDialog = true;
      this.searchQuery = "";
    },
    startProgress() {
      this.progress = 100;
      const totalSteps = this.snackbar.timeout / 100;
      let currentStep = totalSteps;

      if (this.interval) clearInterval(this.interval);

      this.interval = setInterval(() => {
        currentStep--;
        this.progress = (currentStep / totalSteps) * 100;

        if (currentStep <= 0) {
          clearInterval(this.interval);
        }
      }, 100);
    },
    handleButtonClick(event) {
      console.log("Assistant handleButtonClick", event)
      const target = event.target.closest("[data-action]");
      if (!target) return;

      const action = target.getAttribute("data-action");

      if (action === "set-alert") {
        this.setAlert(
          target.getAttribute("data-chatid"),
          target.getAttribute("data-chatuuid"),
          true
        );
      } else if (action === "show-snackbar") {
        const message = target.getAttribute("data-message") || "Information";
        this.snackbar.message = message;
        this.snackbar.timeout = 10000;
        this.snackbar.show = true;
      }
    },
    async saveReport() {
      try {
        const { chat_id, chat_uuid, is_alert_set } = this.saveAsReport;
        let response;
        if (is_alert_set === true) {
          response = await this.$apollo.query({
            query: SAVE_USER_CHAT,
            variables: {
              chat_id,
              chat_uuid,
              is_alert_set,
              chat_title: this.savedReportTitle,
            },
            fetchPolicy: "no-cache",
          });
        } else {
          response = await this.$apollo.query({
            query: UNSAVE_USER_CHAT,
            variables: {
              chat_id,
              chat_uuid,
              is_alert_set,
            },
            fetchPolicy: "no-cache",
          });
        }
        await this.loadChat(chat_uuid);
        is_alert_set === true
          ? (this.showReportTitleDialog = false)
          : (this.showUnSaveDialog = false);
        const message =
          is_alert_set === true ? "Saved as Report" : "Removed Report";
        this.snackbar.show = true;
        this.snackbar.message = message;
        this.snackbar.timeout = is_alert_set === true ? 4000 : 3000;
      } catch (e) {
        console.error(e);
      }
    },
    async setAlert(chat_id, chat_uuid, is_alert_set) {
      this.saveAsReport.chat_id = chat_id;
      this.saveAsReport.chat_uuid = chat_uuid;
      this.saveAsReport.is_alert_set = is_alert_set;
      is_alert_set === true
        ? (this.showReportTitleDialog = true)
        : (this.showUnSaveDialog = true);
    },
    startSpeechRecognition() {
      if (!this.isMicSupported) return;

      const recognition = new webkitSpeechRecognition();
      recognition.lang = 'en-US';
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.onresult = (event) => {
        this.userInput += (this.userInput ? '\n' : '') + event.results[0][0].transcript;
      };
      recognition.start();
    },
    async sendMessage() {
      if (!this.userGoogle) {
        this.snackbar.color = "error";
        this.snackbar.message = "Sign In needed";
        this.snackbar.show = true;
        return;
      }
      if (!this.userInput.trim()) return;
      if (this.userInput.length > 1000)
        this.userInput = this.userInput.substring(0, 1000);
      this.suggestions = [];
      const userMessage = {
        role: "user",
        content: this.userInput.trim(),
      };

      // Add user message to chat
      this.messages.push(userMessage);
      this.userInput = "";
      this.isLoading = true;
      const chatSessionId = sessionStorage.getItem("chatSessionId");
      if (!chatSessionId) {
        this.snackbar.show = true;
        this.snackbar.message = "Chat session not found";
        return;
      }
      try {
        const response = await fetch("/api/chat/reasoning", {
          method: "POST",

          headers: {
            "Content-Type": "application/json",
            Accept: "text/event-stream",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            chatSessionId,
            country: this.country,
            activity: "capital_market_chat",
            messages: [...this.messages],
          }),
        });
        if (response.status !== 200) {
          this.snackbar.color = "error";
          this.snackbar.message = "Not authorized";
          this.snackbar.show = true;
          return;
        }
        // Create reader from response body
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let currentResponse = "";

        // Read the stream
        while (true) {
          const res = await reader.read();
          const { done, value } = res;

          if (done) {
            break;
          }

          // Decode the chunk and split into lines
          const chunk = decoder.decode(value);
          const lines = chunk.split("\n");
          for (const line of lines) {
            if (line.startsWith("data: ")) {
              try {
                const data = JSON.parse(line.slice(5));
                if (data.error) {
                  this.showError = true;
                  this.errorMessage = data.error;
                  break;
                }
                //if (data.chatId) this.currentChatId = chatId;
                currentResponse += data.response + "\n";
                // Update the UI with the streaming response
                if (
                  this.messages[this.messages.length - 1]?.role === "assistant"
                ) {
                  this.messages[this.messages.length - 1].content =
                    currentResponse;
                } else {
                  this.messages.push({
                    role: "assistant",
                    content: currentResponse,
                  });
                }
                if (data.chat_id)
                  this.messages[this.messages.length - 1].id = data.chat_id;
                if (data.chat_uuid)
                  this.messages[this.messages.length - 1].chat_uuid =
                    data.chat_uuid;
                if (data.done) {
                  break;
                }
              } catch (e) {
                console.error("Error parsing JSON:", e);
              }
            }
          }
        }
      } catch (error) {
        console.error("Error:", error);
        this.errorMessage = "Failed to get response from the assistant";
        this.showError = true;
      } finally {
        this.isLoading = false;
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }
      await this.getUserChatHistory();
    },
    scrollToBottom() {
      const container = this.$refs.messagesContainer;
      if (container) {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: "smooth",
        });
      }
      this.$nextTick(() => {
        this.$refs.chatInput.focus();
      });
    },
  },
  computed: {
    ...mapState(["loggedInGoogle", "userGoogle", "country"]),
    processedMessages() {
    return this.messages.map((msg) => {
      return {
        ...msg,
        content: this.decodeUnicodeEscapes(msg.content),
      };
    });
  },
    filteredChats() {
      if (!this.searchQuery) return this.chatHistory;
      return this.chatHistory.filter((chat) =>
        chat.title.toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    },
  },
};
</script>
<style scoped>
.chat-wrapper {
  height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
}

.chat-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  margin: 8px;
  max-height: calc(100vh - 130px);
}

.chat-header {
  flex-shrink: 0;
}
.subtitle-wrap {
  white-space: pre-wrap;
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
}
.messages-wrapper {
  flex: 1;
  overflow-y: auto;
  min-height: 0; /* This is important for Firefox */
  height: calc(100vh - 180px);
}

.chat-messages {
  height: 100%;
}

.chat-input {
  flex-shrink: 0;
  background: white;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}

/* New styles for the inline suggestion */
.position-relative {
  position: relative;
  width: 100%;
}

.suggestion-container {
  position: relative;
}

.suggestion-inline {
  position: absolute;
  pointer-events: none;
  white-space: pre;
  z-index: 5;
}

.suggestion-text {
  color: #888;
  font-family: inherit;
  font-size: inherit;
}

/* Rest of existing styles */
@media (max-width: 960px) {
  .chat-input {
    margin-left: 8px;
  }
}
.message-line {
  margin: 0; /* Remove default paragraph margins */
  min-height: 1.2em; /* Preserve empty lines */
}
.message-item {
  padding: 4px 64px; /* Reduce default v-list-item padding */
}

.message-card {
  max-width: 75%;
  border-radius: 8px;
  padding: 8px;
}

.user-message {
  margin-left: auto;
  background-color: #e3f2fd; /* Light blue */
}

.assistant-message {
  background-color: #f5f5f5; /* Light grey */
}

.white-space-pre {
  white-space: pre-wrap;
  word-break: break-word;
}
.red-button {
  background-color: #ff3b30;
  color: white;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: background-color 0.2s;
}

.red-button:hover {
  background-color: #ff6651;
}

.red-button:active {
  background-color: #e0352b;
  transform: translateY(1px);
}
.chat-wrapper {
  display: flex;
  height: 100vh;
}
.sidebar {
  background-color: #f5f5f5;
}
.chat-list-item {
  position: relative;
}
</style>